import { breadcrumb } from '../../packages/sentry';

let appPrefix = '';

const OBSERVER_COMPONENTS = ['ValidationObserver', 'ValidationProvider'];

function toSnakeCase(name) {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .toLowerCase();
}

/**
 * App names are hyphenated. GA4 discards a non-alphanumeric event name and still
 * answers 2xx, so an unnormalized prefix loses the app's whole funnel silently.
 */
export function setAppPrefix(name) {
  appPrefix = name ? `${toSnakeCase(name)}_` : '';
}

/**
 * Landmine: walk `$vnode.context`, not `$parent`. For slot content Vue 2 sets `$parent`
 * to the slot host, merging every form sharing that panel into one funnel.
 */
export function resolveFormId(vm) {
  if (!vm) {
    return null;
  }

  for (let node = vm; node; node = (node.$vnode && node.$vnode.context) || node.$parent) {
    const options = node.$options;
    if (!options) {
      continue;
    }

    if (options.analyticsFormId) {
      return appPrefix + toSnakeCase(options.analyticsFormId);
    }

    if (options.name && !OBSERVER_COMPONENTS.includes(options.name)) {
      return appPrefix + toSnakeCase(options.name);
    }
  }

  return null;
}

/**
 * Landmine: `dataLayer.push` is GTM's own implementation, not the array method. It runs
 * matching tags synchronously, so a throwing tag lands here — and every caller sits
 * mid-journey, before a redirect that would then never happen.
 */
function push(params) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(params);
  } catch (error) {
    breadcrumb('form-analytics', 'error', 'dataLayer push failed', {
      event: params && params.event,
      error: String(error),
    });
  }
}

function pushFormEvent(formId, type, params = {}) {
  if (!formId) {
    breadcrumb('form-analytics', 'warning', 'form reported no id, event dropped', { type });

    return;
  }

  push({ event: `${formId}_${type}`, form_id: formId, ...params });
}

function toFormId(target) {
  return typeof target === 'string' ? appPrefix + target : resolveFormId(target);
}

export function trackFormLoad(target) {
  pushFormEvent(toFormId(target), 'load');
}

const MAX_VALUE_LENGTH = 100;

/**
 * Landmine: GA4 drops a whole parameter value over MAX_VALUE_LENGTH and still answers
 * 2xx, so joining every field name loses `reason` entirely on the largest failures —
 * AddAPetForm's 16 fields reach 194 characters. Truncate to whole names; `field_count`
 * stays exact, so a short `reason` reads as a prefix rather than the full story.
 */
function withinValueLimit(fields) {
  const kept = [];
  let length = 0;

  for (const field of fields) {
    const next = length === 0 ? field.length : length + 1 + field.length;

    if (next > MAX_VALUE_LENGTH) {
      break;
    }

    kept.push(field);
    length = next;
  }

  return kept.join(',');
}

/**
 * Field names only: vee-validate messages interpolate the rejected value, so
 * reporting them would leak what the user typed.
 *
 * @param {Object.<string, string[]>} errors vee-validate ValidationObserver errors bag
 */
export function trackFormValidationError(target, errors) {
  const failed = Object.keys(errors || {})
    .filter(field => (errors[field] || []).length > 0)
    .sort();

  if (failed.length === 0) {
    return;
  }

  pushFormEvent(toFormId(target), 'error', {
    reason: withinValueLimit(failed),
    field_count: failed.length,
  });
}

export function trackFormHandlerError(target, error) {
  pushFormEvent(toFormId(target), 'error', {
    reason: error && error.name ? toSnakeCase(error.name) : 'error',
  });
}
